import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "../../services/api";
import ActivityLogCard from "./ActivityLogCard";
import DashboardLoader from "./DashboardLoader";
import FilterDropdown from "./FilterDropdown";
import FilterDatePicker from "./FilterDatePicker";
import { HiMenuAlt2, HiViewGrid } from "react-icons/hi";

const ActivityManagement = () => {
    const [limit] = useState(50);
    // Filters state (visual only for now as API might not support it yet, but UI must be there)
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
    const [filterAction, setFilterAction] = useState("All Actions");
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");

    const { data: logs, isLoading } = useQuery({
        queryKey: ["adminActivityLogs", limit],
        queryFn: async () => {
            const res = await apiRequest(`/activity/all?limit=${limit}`);
            return res.data;
        },
    });

    if (isLoading) return <DashboardLoader />;

    // Client-side filtering if API doesn't support it yet
    const filteredLogs = logs?.filter(log => {
        if (filterAction !== "All Actions" && log.action !== filterAction) return false;

        if (dateStart) {
            const logDate = new Date(log.created_at).toISOString().split('T')[0];
            if (logDate < dateStart) return false;
        }
        if (dateEnd) {
            const logDate = new Date(log.created_at).toISOString().split('T')[0];
            if (logDate > dateEnd) return false;
        }

        return true;
    });

    const uniqueActions = ["All Actions", ...new Set(logs?.map(l => l.action) || [])];

    return (
        <section className="flex h-full flex-col gap-6 px-6 py-6 font-primary">
            {/* Page Header */}
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                <div>
                    {/* Breadcrumbs - Simulating the "Home > Activity Logs" from screenshot */}
                    <div className="mb-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>Home</span>
                        <span>&gt;</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-200">Activity Logs</span>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Activity Logs</h1>
                </div>
            </div>

            {/* Filter / Controls Bar */}
            <div className="flex flex-col flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100 dark:bg-dashboardBg dark:ring-gray-800 md:flex-row">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">System Activity Logs</h2>

                <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
                    {/* View Toggle */}
                    <div className="flex items-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-zinc-800">
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                        >
                            <HiMenuAlt2 className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                        >
                            <HiViewGrid className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Date Inputs */}
                    <div className="flex items-center gap-2">
                        <FilterDatePicker
                            value={dateStart}
                            onChange={setDateStart}
                            placeholder="Start date"
                        />
                        <span className="text-gray-400">-</span>
                        <FilterDatePicker
                            value={dateEnd}
                            onChange={setDateEnd}
                            placeholder="End date"
                        />
                    </div>

                    {/* Action Dropdown */}
                    <FilterDropdown
                        options={uniqueActions}
                        value={filterAction}
                        onChange={setFilterAction}
                        label="Filter Actions"
                    />
                </div>
            </div>

            {/* Content Grid */}
            <div className={`grid gap-5 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2' : 'grid-cols-1'}`}>
                {filteredLogs && filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                        <ActivityLogCard key={log.id} log={log} />
                    ))
                ) : (
                    <div className="col-span-full py-10 text-center text-gray-500 dark:text-gray-400">
                        No activity logs found matching your filters.
                    </div>
                )}
            </div>

        </section>
    );
};

export default ActivityManagement;
